// SkinsCard — read-only Skins game display.
//
// WS-C Chunk 8. Takes a game_id, calls get_skins_state, renders:
//   • Running totals (rank + skin count + $/skin display value)
//   • Per-hole grid: hole # · par · scores · winner or "TIE +N carryover"
//
// The compute is server-side (db.js get_skins_state) — this is a pure
// display component. Refresh is manual (parent decides when to reload).
//
// CLAUDE.md hard rule surfaced in the copy: "TournyFlex never touches
// wager money — the $ column is display-only. Players settle
// themselves after the round."

import { useCallback, useEffect, useState } from 'react'
import { db } from '../lib/db'

function pctChange() { /* reserved for future carry-open animation */ }
void pctChange

export default function SkinsCard({ game_id, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load silently on interval refreshes — only flip the loading flag
  // on the FIRST fetch. Prior version toggled loading on every refresh
  // and produced a visible "Loading Skins…" flash every autoRefreshMs.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_skins_state', { game_id })
      setState(data)
      setError(null)
    } catch (e) {
      setError(e.message || 'Skins compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  if (loading) {
    return (
      <div className="card">
        <p className="text-muted text-sm" style={{ margin: 0 }}>Loading Skins…</p>
      </div>
    )
  }
  if (error || !state) {
    return (
      <div className="card">
        <p className="text-sm" style={{ margin: 0, color: 'var(--danger, #b23)' }}>
          {error || 'No Skins data yet.'}
        </p>
      </div>
    )
  }
  if (state.note === 'need_two_participants') {
    return (
      <div className="card">
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Skins needs at least 2 participants.
        </p>
      </div>
    )
  }

  const { participants, holes, totals, carry_open, resolvable_up_to, game } = state
  const unitValue = Number(game?.config?.unit_value) || 0

  // Rank participants by skins won (desc), then name (stable).
  const nameById = new Map(participants.map(p => [p.player_id, p.name]))
  const rows = participants
    .map(p => ({
      player_id: p.player_id,
      name: p.name,
      skins: totals[p.player_id]?.skins || 0,
      units: totals[p.player_id]?.units || 0,
    }))
    .sort((a, b) => (b.skins - a.skins) || a.name.localeCompare(b.name))

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h3 style={{ margin: 0 }}>Skins</h3>
        <span className="text-xs text-muted">
          {resolvable_up_to} hole{resolvable_up_to === 1 ? '' : 's'} scored
          {carry_open > 0 && <> · <strong style={{ color: 'var(--gold)' }}>{carry_open} carrying</strong></>}
        </span>
      </div>

      {unitValue > 0 && (
        <p className="text-xs text-muted" style={{ marginBottom: 8 }}>
          ${unitValue.toFixed(2)}/skin (display only — settle amongst yourselves)
        </p>
      )}

      <div style={{ display: 'grid', gap: 4, marginBottom: 10 }}>
        {rows.map((r, i) => (
          <div key={r.player_id} style={{
            display: 'grid',
            gridTemplateColumns: '24px 1fr auto auto',
            alignItems: 'center', gap: 8,
            padding: '6px 8px',
            background: i === 0 ? 'var(--green-deep)' : 'transparent',
            borderRadius: 6,
          }}>
            <span className="text-xs text-muted" style={{ fontFamily: 'var(--font-mono)' }}>
              {i + 1}
            </span>
            <span className="text-sm" style={{ fontWeight: i === 0 ? 600 : 400 }}>
              {r.name}
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              color: r.skins > 0 ? 'var(--gold)' : 'var(--gray-400)',
            }}>
              {r.skins}
            </span>
            {unitValue > 0 && (
              <span className="text-xs text-muted" style={{ fontFamily: 'var(--font-mono)', minWidth: 44, textAlign: 'right' }}>
                ${r.units.toFixed(2)}
              </span>
            )}
          </div>
        ))}
      </div>

      {holes.length > 0 && (
        <>
          <p className="text-xs text-muted" style={{ marginTop: 4, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Per hole
          </p>
          <div style={{ overflowX: 'auto', margin: '0 -12px' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--green-mid)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Hole</th>
                  <th style={{ textAlign: 'left', padding: '6px 4px' }}>Par</th>
                  {participants.map(p => (
                    <th key={p.player_id} style={{ padding: '6px 4px', textAlign: 'center', maxWidth: 60, whiteSpace: 'nowrap' }}>
                      {p.name.split(' ')[0]}
                    </th>
                  ))}
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Winner</th>
                </tr>
              </thead>
              <tbody>
                {holes.map(h => (
                  <tr key={h.hole} style={{ borderBottom: '1px solid var(--green-dark)' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{h.hole}</td>
                    <td style={{ padding: '4px 4px', fontFamily: 'var(--font-mono)', color: 'var(--gray-500)' }}>
                      {h.par || '–'}
                    </td>
                    {participants.map(p => {
                      const s = h.scores?.[p.player_id]
                      const isWinner = h.winner_id === p.player_id
                      const isTie = Array.isArray(h.tie) && h.tie.includes(p.player_id)
                      return (
                        <td key={p.player_id} style={{
                          padding: '4px 4px', textAlign: 'center',
                          fontFamily: 'var(--font-mono)',
                          fontWeight: isWinner || isTie ? 700 : 400,
                          color: isWinner ? 'var(--gold)' : isTie ? 'var(--gray-300)' : 'inherit',
                        }}>
                          {s ?? '–'}
                        </td>
                      )
                    })}
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {h.winner_id
                        ? <span style={{ color: 'var(--gold)', fontWeight: 600 }}>
                            +{h.skins_awarded}
                            {h.carryover_in > 0 && (
                              <span className="text-xs text-muted"> ({h.carryover_in} carried)</span>
                            )}
                          </span>
                        : <span className="text-xs text-muted">
                            TIE — +{h.carryover_in + 1} to next
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
